
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSettings } from "@/providers/SettingsProvider";
import { Settings, Clock, Calendar, Palette, Users, X, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { TIMEZONE_OPTIONS } from "@/utils/settings";

interface CalendarSettingsProps {
	isOpen: boolean;
	onClose: () => void;
}

const DAYS_OF_WEEK = [
	{ value: "0", label: "Sunday" },
	{ value: "1", label: "Monday" },
	{ value: "2", label: "Tuesday" },
	{ value: "3", label: "Wednesday" },
	{ value: "4", label: "Thursday" },
	{ value: "5", label: "Friday" },
	{ value: "6", label: "Saturday" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
	value: i.toString(),
	label: i.toString().padStart(2, '0') + ':00',
}));

const EVENT_DURATIONS = [
	{ value: "15", label: "15 minutes" },
	{ value: "30", label: "30 minutes" },
	{ value: "60", label: "1 hour" },
	{ value: "120", label: "2 hours" },
];

const EVENT_COLORS = [
	{ value: "#9b87f5", label: "Purple" },
	{ value: "#1EAEDB", label: "Blue" },
	{ value: "#F97316", label: "Orange" },
	{ value: "#D946EF", label: "Pink" },
	{ value: "#0EA5E9", label: "Sky" },
	{ value: "#8E9196", label: "Gray" },
];

export const CalendarSettings = ({ isOpen, onClose }: CalendarSettingsProps) => {
	const { t } = useTranslation();
	const { 
		calendarSettings,
		updateCalendarSettings,
		isSaving,
		timezone,
		setTimezoneValue
	} = useSettings();

	// Local state to track changes before saving
	const [localSettings, setLocalSettings] = useState(calendarSettings);

	// Reset local settings when modal opens
	useEffect(() => {
		setLocalSettings(calendarSettings);
	}, [isOpen, calendarSettings]);

	const handleWorkWeekDayToggle = (day: string) => {
		const currentDays = localSettings?.workWeekDays || [];
		const newDays = currentDays.includes(day)
			? currentDays.filter(d => d !== day)
			: [...currentDays, day].sort();
		
		setLocalSettings({
			...localSettings,
			workWeekDays: newDays
		});
	};

	const handleSave = async () => {
		try {
			await updateCalendarSettings(localSettings);
			toast.success("Calendar settings saved successfully");
			onClose();
		} catch (error) {
			console.error("Error saving settings:", error);
			toast.error("Failed to save settings");
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-[800px] w-[90vw] mx-auto max-h-[85vh] overflow-hidden p-0 flex flex-col">
				<DialogHeader className="p-6 pb-4 border-b border-gray-200 flex-shrink-0">
					<DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-3">
						<div className="p-2 bg-blue-100 rounded-sm">
							<Settings className="h-5 w-5 text-blue-500" />
						</div>
						{t("calendar.settings.title", "Calendar Settings")}
					</DialogTitle>
				</DialogHeader>

				<div className="flex-1 overflow-y-auto p-6 min-h-0">
					<div className="space-y-8">
						{/* Timezone Section */}
						<div className="space-y-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-indigo-50 rounded-sm">
									<Globe className="h-4 w-4 text-indigo-600" />
								</div>
								<h3 className="text-lg font-semibold text-gray-900">
									{t("calendar.settings.timezone", "Timezone")}
								</h3>
							</div>
							<div className="space-y-3">
								<Label className="text-sm font-medium text-gray-700">
									{t("calendar.settings.timezone", "Timezone")}
								</Label>
								<Select value={timezone} onValueChange={setTimezoneValue}>
									<SelectTrigger className="h-11">
										<SelectValue placeholder={t("calendar.settings.selectTimezone", "Select timezone")} />
									</SelectTrigger>
									<SelectContent className="max-h-[300px]">
										{TIMEZONE_OPTIONS.map((option) => (
											<SelectItem key={option.id} value={option.id}>
												<span className="flex justify-between w-full">
													<span>{option.name}</span>
													<span className="text-gray-500">GMT{option.offset}</span>
												</span>
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>

						{/* Work Hours Section */}
						<div className="space-y-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-blue-50 rounded-sm">
									<Clock className="h-4 w-4 text-blue-500" />
								</div>
								<h3 className="text-lg font-semibold text-gray-900">
									{t("calendar.settings.workHours", "Work Hours")}
								</h3>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div className="space-y-3">
									<Label className="text-sm font-medium text-gray-700">
										{t("calendar.settings.workDayStart", "Work day starts")}
									</Label>
									<Select
										value={localSettings?.workDayStart?.toString()}
										onValueChange={(value) => setLocalSettings({
											...localSettings,
											workDayStart: parseInt(value)
										})}
									>
										<SelectTrigger className="h-11">
											<SelectValue placeholder={t("calendar.settings.selectStartTime", "Select start time")} />
										</SelectTrigger>
										<SelectContent>
											{HOURS.map((hour) => (
												<SelectItem key={hour.value} value={hour.value}>
													{hour.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								
								<div className="space-y-3">
									<Label className="text-sm font-medium text-gray-700">
										{t("calendar.settings.workDayEnd", "Work day ends")}
									</Label>
									<Select
										value={localSettings?.workDayEnd?.toString()}
										onValueChange={(value) => setLocalSettings({
											...localSettings,
											workDayEnd: parseInt(value)
										})}
									>
										<SelectTrigger className="h-11">
											<SelectValue placeholder={t("calendar.settings.selectEndTime", "Select end time")} />
										</SelectTrigger>
										<SelectContent>
											{HOURS.map((hour) => (
												<SelectItem key={hour.value} value={hour.value}>
													{hour.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>
						</div>

						{/* Work Week Section */}
						<div className="space-y-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-green-50 rounded-sm">
									<Calendar className="h-4 w-4 text-green-600" />
								</div>
								<h3 className="text-lg font-semibold text-gray-900">
									{t("calendar.settings.workWeek", "Work Week")}
								</h3>
							</div>
							<div className="space-y-4">
								<div className="space-y-3">
									<Label className="text-sm font-medium text-gray-700">
										{t("calendar.settings.workWeekDays", "Work week days")}
									</Label>
									<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
										{DAYS_OF_WEEK.map((day) => (
											<div key={day.value} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-sm hover:bg-gray-100 transition-colors">
												<Checkbox
													id={`day-${day.value}`}
													checked={localSettings?.workWeekDays?.includes(day.value)}
													onCheckedChange={() => handleWorkWeekDayToggle(day.value)}
												/>
												<label
													htmlFor={`day-${day.value}`}
													className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
												>
													{day.label}
												</label>
											</div>
										))}
									</div>
								</div>
								
								<div className="space-y-3">
									<Label className="text-sm font-medium text-gray-700">
										{t("calendar.settings.firstDayOfWeek", "First day of week")}
									</Label>
									<Select
										value={localSettings?.firstDayOfWeek?.toString()}
										onValueChange={(value) => setLocalSettings({
											...localSettings,
											firstDayOfWeek: parseInt(value)
										})}
									>
										<SelectTrigger className="h-11">
											<SelectValue placeholder={t("calendar.settings.selectFirstDay", "Select first day")} />
										</SelectTrigger>
										<SelectContent>
											{DAYS_OF_WEEK.map((day) => (
												<SelectItem key={day.value} value={day.value}>
													{day.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>
						</div>



						{/* Event Defaults Section */}
						<div className="space-y-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-purple-50 rounded-sm">
									<Palette className="h-4 w-4 text-purple-600" />
								</div>
								<h3 className="text-lg font-semibold text-gray-900">
									{t("calendar.settings.eventDefaults", "Event Defaults")}
								</h3>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div className="space-y-3">
									<Label className="text-sm font-medium text-gray-700">
										{t("calendar.settings.defaultEventDuration", "Default event duration")}
									</Label>
									<Select
										value={localSettings?.defaultEventDuration?.toString()}
										onValueChange={(value) => setLocalSettings({
											...localSettings,
											defaultEventDuration: parseInt(value)
										})}
									>
										<SelectTrigger className="h-11">
											<SelectValue placeholder={t("calendar.settings.selectDuration", "Select duration")} />
										</SelectTrigger>
										<SelectContent>
											{EVENT_DURATIONS.map((duration) => (
												<SelectItem key={duration.value} value={duration.value}>
													{duration.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								
								<div className="space-y-3">
									<Label className="text-sm font-medium text-gray-700">
										{t("calendar.settings.defaultEventColor", "Default event color")}
									</Label>
									<Select
										value={localSettings?.defaultEventColor || "#9b87f5"}
										onValueChange={(value) => setLocalSettings({
											...localSettings,
											defaultEventColor: value
										})}
									>
										<SelectTrigger className="h-11">
											<SelectValue placeholder={t("calendar.settings.selectColor", "Select color")} />
										</SelectTrigger>
										<SelectContent>
											{EVENT_COLORS.map((color) => (
												<SelectItem key={color.value} value={color.value}>
													<div className="flex items-center gap-3">
														<div 
															className="w-5 h-5 rounded-sm border-2 border-gray-200" 
															style={{ backgroundColor: color.value }}
														/>
														<span className="font-medium">{color.label}</span>
													</div>
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>
						</div>

					</div>
				</div>
				
				{/* Footer */}
				<div className="flex flex-col sm:flex-row justify-end p-4 lg:p-6 border-t border-gray-200 gap-3 bg-gradient-to-r from-gray-50 to-white flex-shrink-0 mt-auto">
					<Button
						type="button"
						variant="outline"
						onClick={onClose}
						className="w-full sm:w-auto px-6 py-2.5 border-gray-300 hover:border-gray-400 hover:bg-gray-50"
					>
						{t("common.cancel", "Cancel")}
					</Button>
					<Button
						onClick={handleSave}
						disabled={isSaving}
						className="w-full sm:w-auto px-6 py-2.5 bg-blue-500 hover:bg-blue-500 text-white"
					>
						{isSaving ? t("common.saving", "Saving...") : t("common.save", "Save changes")}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};
